import type { LucideIcon } from 'lucide-react'
import {
  CarIcon,
  CompassIcon,
  FactoryIcon,
  FuelIcon,
  GraduationCapIcon,
  HomeIcon,
  NetworkIcon,
  PillIcon,
  PlayIcon,
  ServerIcon,
  TrendingUpIcon,
  TruckIcon,
  ZapIcon,
} from 'lucide-react'

export type NavItem = {
  label: string
  to: string
  icon?: LucideIcon
  children?: NavItem[]
  /** When a grouped item sets this, its children render as a second row of
   * real tabs (instead of a dropdown menu) while the group is active. */
  expandTabs?: boolean
}
export type NavPillar = {
  id: string
  label: string
  to: string
  icon?: LucideIcon
  minRole?: 'operator' | 'admin'
  children?: NavItem[]
  match?: string[]
}

export const navConfig: NavPillar[] = [
  { id: 'home', label: 'Home', to: '/', icon: HomeIcon },
  {
    id: 'explore',
    label: 'Discover',
    to: '/hivemqintro',
    icon: CompassIcon,
    match: [
      '/hivemqintro',
      '/hivemqvision',
      '/hivemqpilars',
      '/hivemqedge',
      '/pulse',
      '/valuespace',
      '/hivemqagentic',
    ],
  },
  {
    id: 'industries',
    label: 'Industries',
    to: '/factory',
    icon: FactoryIcon,
    children: [
      { label: 'Factory', to: '/factory', icon: FactoryIcon },
      { label: 'Automotive', to: '/automotive', icon: CarIcon },
      { label: 'Energy', to: '/energy', icon: ZapIcon },
      { label: 'Pharma', to: '/pharma', icon: PillIcon },
      { label: 'Logistics', to: '/logistics', icon: TruckIcon },
      { label: 'Oil & Gas', to: '/oilandgas', icon: FuelIcon },
      { label: 'Connected', to: '/connected', icon: NetworkIcon },
      { label: 'Data Center', to: '/datacenter', icon: ServerIcon },
    ],
  },
  {
    id: 'value',
    label: 'Value',
    to: '/business-value',
    icon: TrendingUpIcon,
    match: ['/customers'],
  },
  {
    id: 'learn',
    label: 'Learn',
    to: '/education',
    icon: GraduationCapIcon,
    // Data Hub now lives inside Learn rather than as its own top-level
    // pillar, so its pages resolve to the Learn section.
    match: [
      '/datahub',
      '/hivemqdatahub',
      '/dhanonymizeflow',
      '/dhftocflow',
      '/dhgeolocationflow',
      '/dhsparkplugfanoutflow',
    ],
  },
  {
    id: 'tools',
    label: 'Tools',
    to: '/playground',
    icon: PlayIcon,
    match: ['/mqtt-client', '/demoinfo'],
  },
]

// A pillar is active when the pathname matches its `to`, any child `to`, or
// any `match` prefix. Root ('/') matches only the exact root path. The most
// specific (longest) matching base wins so /business-value/energy beats '/'.
export function findActivePillar(pathname: string): NavPillar | undefined {
  let best: NavPillar | undefined
  let bestLen = -1
  for (const pillar of navConfig) {
    const bases = [pillar.to, ...(pillar.children?.map((c) => c.to) ?? []), ...(pillar.match ?? [])]
    for (const base of bases) {
      const hit =
        base === '/' ? pathname === '/' : pathname === base || pathname.startsWith(`${base}/`)
      if (hit && base.length > bestLen) {
        best = pillar
        bestLen = base.length
      }
    }
  }
  return best
}
